/**
 * Alert — the one inline message box (something went wrong, be careful, done, FYI). Sits in the page where the
 * problem is; short-lived confirmations use a toast instead. Errors and warnings are announced to screen readers at once.
 */
import * as React from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

const TONES = {
  danger: { icon: XCircle, box: "bg-danger-50 text-danger-800 ring-danger-200", mark: "text-danger-600" },
  warning: { icon: AlertTriangle, box: "bg-warning-50 text-warning-800 ring-warning-200", mark: "text-warning-600" },
  success: { icon: CheckCircle2, box: "bg-success-50 text-success-800 ring-success-200", mark: "text-success-600" },
  info: { icon: Info, box: "bg-info-50 text-info-800 ring-info-200", mark: "text-info-600" },
} as const satisfies Record<string, { icon: LucideIcon; box: string; mark: string }>;

export type AlertTone = keyof typeof TONES;

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  tone: AlertTone;
  title?: React.ReactNode;
  /** A button or link on the right, for the one thing the person can do about it. */
  action?: React.ReactNode;
}

export function Alert({ tone, title, action, className, children, ...props }: AlertProps) {
  const { icon: Icon, box, mark } = TONES[tone];
  return (
    <div
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
      className={cn("flex items-start gap-2.5 rounded-lg px-3.5 py-3 text-sm leading-snug ring-1 ring-inset", box, className)}
      {...props}
    >
      <Icon className={cn("mt-px size-4 shrink-0", mark)} aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && "mt-0.5")}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
